import type { Translate } from "@/app/localization";
import type { BillingCurrencyAmount, BillingTotals } from "@/lib/api";

/** What a billing figure shows: a headline value, the known per-currency amounts when no
 *  single headline exists, and short human notes. */
export interface BillingFigure {
  value: string;
  parts: string[];
  notes: string[];
}

export const UNKNOWN = "—";

// Amounts arrive as cents / 100, so exactly two fraction digits: never re-rounded to a
// currency's Intl default (JPY would turn 12.50 into ¥13).
const CENTS = { minimumFractionDigits: 2, maximumFractionDigits: 2 } as const;

export function formatAmount(amount: number, currency: string | null): string {
  const plain = amount.toLocaleString(undefined, CENTS);
  if (!currency) return plain;
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency, ...CENTS }).format(amount);
  } catch (e) {
    // Not an ISO 4217 code (e.g. "credits"): show the amount with the code as given.
    if (e instanceof RangeError) return `${plain} ${currency}`;
    throw e;
  }
}

export function formatNumber(value: number | null | undefined, digits = 0): string {
  if (value == null) return UNKNOWN;
  return value.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

/** Before the first published pass every figure is unknown, not zero: either still being
 *  calculated, or unavailable (no calculation running). */
export function totalsState(t: Pick<BillingTotals, "cache_updated_at" | "computing">): "calculating" | "unavailable" | "ready" {
  if (t.cache_updated_at != null) return "ready";
  return t.computing ? "calculating" : "unavailable";
}

function amounts(list: BillingCurrencyAmount[]): string[] {
  return list.map((a) => formatAmount(a.amount, a.currency));
}

export function finalizedFigure(t: Translate, totals: BillingTotals): BillingFigure {
  const notes: string[] = [];
  const unresolved = totals.unresolved_billing_count ?? 0;
  if (unresolved > 0) notes.push(t("operator.pricing.billing_finalized_unresolved_count", { count: unresolved }));
  if (totals.finalized_billed.length > 1) notes.push(t("operator.pricing.billing_currencies_separate"));
  if (totals.total_revenue != null) {
    return { value: formatAmount(totals.total_revenue, totals.revenue_currency), parts: [], notes };
  }
  return { value: UNKNOWN, parts: amounts(totals.finalized_billed), notes };
}

export function activeEstimateFigure(t: Translate, totals: BillingTotals): BillingFigure {
  const notes: string[] = [];
  if (totals.active_estimate_status === "no_active_session") notes.push(t("operator.pricing.billing_active_no_sessions"));
  const unknown = totals.active_estimate_unknown_count ?? 0;
  if (unknown > 0) notes.push(t("operator.pricing.billing_active_unknown_count", { count: unknown }));
  else if (totals.active_estimate_status === "unavailable") notes.push(t("operator.pricing.billing_active_unavailable"));
  if (totals.active_estimated_revenue != null) {
    return { value: formatAmount(totals.active_estimated_revenue, totals.active_estimate_currency), parts: [], notes };
  }
  if (totals.active_estimated.length > 1) notes.push(t("operator.pricing.billing_currencies_separate"));
  return { value: UNKNOWN, parts: amounts(totals.active_estimated), notes };
}

/** Why the shown totals may not be current; null when they are a complete, fresh pass. */
export function freshnessNote(
  t: Translate,
  totals: Pick<BillingTotals, "cache_updated_at" | "refresh_error" | "stale" | "computing" | "complete">,
  refreshFailed: boolean,
): string | null {
  const time = totals.cache_updated_at
    ? new Date(totals.cache_updated_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })
    : null;
  if (refreshFailed || totals.refresh_error) {
    return time
      ? t("operator.pricing.billing_freshness_refresh_failed_as_of", { time })
      : t("operator.pricing.billing_freshness_refresh_failed");
  }
  if (totals.stale) {
    return time ? t("operator.pricing.billing_freshness_stale_as_of", { time }) : t("operator.pricing.billing_freshness_stale");
  }
  if (totals.computing) {
    return time ? t("operator.pricing.billing_freshness_updating_as_of", { time }) : t("operator.pricing.billing_freshness_updating");
  }
  if (!totals.complete) return t("operator.pricing.billing_freshness_incomplete");
  return null;
}
